import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../css/booking.css";
import TherapistCard from "../components/TherapistCard";
import DateCard from "../components/DateCard";
import TimeSlotCard from "../components/TimeSlotCard";
import instance from "../api/axios";
import mixpanel from "../mixpanel";
import Popup from "../components/Popup";
import SessionDetail from "../components/SessionDetail/sessionDetail";
import AboutPhysica from "../components/About/aboutPhysica";
import useDraggableScroll from "../hooks";
import BackHeader from "../components/Home/BackHeader";
import StepIndicator from "../components/Home/StepIndicator";
import rightIcon from "../assets/images/right-arrow.png";

const SlotPageFreelancer = () => {
    const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } = useDraggableScroll();
    const [therapists, setTherapists] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [allTimeSlots, setAllTimeSlots] = useState({});
    const [showPopup, setShowPopup] = useState(false);
    const [showTherapistSection, setShowTherapistSection] = useState(true);
    const [allAvailableDates, setAllAvailableDates] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const navigate = useNavigate();

    const getUser = sessionStorage.getItem("user");
    const { pinCode } = JSON.parse(getUser);

    const generateFixedSlotsForDate = (date, therapist_id) => {
        const slots = [];
        for (let hour = 7; hour <= 21; hour++) {
            for (let min of [0, 30]) {
                const slotTime = new Date(date);
                slotTime.setHours(hour, min, 0, 0);
                slots.push({
                    time: slotTime.toISOString(),
                    available: 1,
                    therapist_id,
                });
            }
        }
        return slots;
    };


    const buildTimeSlotsForAllTherapists = (therapistsList) => {
        const slotsByDate = {};
        const today = new Date();
        for (let i = 0; i < 3; i++) {
            const dateObj = new Date(today);
            dateObj.setDate(today.getDate() + i);
            const dateStr = dateObj.toISOString().split("T")[0];
            slotsByDate[dateStr] = [];

            therapistsList.forEach((therapist) => {
                const slots = generateFixedSlotsForDate(dateStr, therapist.doctor_id);
                slotsByDate[dateStr].push(...slots);
            });
        }
        return slotsByDate;
    };


    const fetchFreelanceTherapists = async () => {
        try {
            const res = await instance.get(`/getFreelanceTherapists?pinCode=${pinCode}`);
            const list = res.data.therapists || [];
            setTherapists(list);

            const fixedSlots = buildTimeSlotsForAllTherapists(list);
            setAllTimeSlots(fixedSlots);

            const availableDates = Object.keys(fixedSlots).map((date) => ({
                date,
                slots: fixedSlots[date].length > 0,
            }));
            setAllAvailableDates(availableDates);
        } catch (err) {
            console.error("Error fetching freelance therapists", err);
        }
    };

    useEffect(() => {
        fetchFreelanceTherapists();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setShowPopup(true), 12000);
        return () => clearTimeout(timer);
    }, []);

    const handleTimeSlotSelection = (slot) => {
        setSelectedTimeSlot(slot);
        setSelectedDoctorId(slot.therapist_id);
        mixpanel.track("Freelance - Time Slot Selected", {
            time: slot.time,
            therapist_id: slot.therapist_id,
        });
    };

    const renderTimeSlots = (start, end) => {
        let slots = allTimeSlots[selectedDate] || [];

        if (selectedDoctorId) {
            slots = slots.filter((slot) => slot.therapist_id === selectedDoctorId);
        } else {
            // Deduplicate slots by time when no therapist is selected
            const seenTimes = new Set();
            slots = slots.filter((slot) => {
                const t = new Date(slot.time).toISOString();
                if (seenTimes.has(t)) return false;
                seenTimes.add(t);
                return true;
            });
        }

        return slots
            .filter((slot) => {
                const hour = new Date(slot.time).getHours();
                return hour >= start && hour < end;
            })
            .map((slot) => (
                <TimeSlotCard
                    key={`${slot.therapist_id}-${slot.time}`}
                    time={slot.time}
                    available={slot.available}
                    isSelected={
                        selectedTimeSlot?.time === slot.time &&
                        selectedTimeSlot?.therapist_id === slot.therapist_id
                    }
                    onClick={() => handleTimeSlotSelection(slot)}
                />
            ));
    };

    const handleProceed = () => {
        if (selectedTimeSlot) {
            navigate("/freelance-booking-detail", {

                state: {
                    therapistId: selectedDoctorId,
                    timing: selectedTimeSlot,
                },

            });
        }
    };

    return (
        <>

            <div className="BookingContainer">
                <div
                    className="BookingWrapper"
                    style={{
                        backgroundImage: `url(${require("../assets/images/BackgroundBig.png")})`,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                    }}
                >

                    <BackHeader onBack={() => navigate(-1)} />
                    <StepIndicator step={2} totalSteps={4} header="Select A Time Slot" />

                    <AboutPhysica
                        handleChooseTherapistClick={() => setShowTherapistSection((prev) => !prev)}
                        showMore={false}
                        pinCode={pinCode}
                    />

                    {showTherapistSection && (
                        <div className="ChooseTherapistContainer">
                            <h1>Select A Freelancer (Optional)</h1>
                            <input
                                type="text"
                                placeholder="Search therapist by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border rounded-lg p-2 w-full mt-2 mb-4"
                            />
                            <div className="ChooseTherapistWrapper">
                                <div className="TherapistCards" ref={ref} onMouseDown={onMouseDown} onMouseLeave={onMouseLeave} onMouseUp={onMouseUp} onMouseMove={onMouseMove}>
                                    {therapists
                                        .filter((therapist) =>
                                            therapist.doctor_name?.toLowerCase().includes(searchTerm.toLowerCase())
                                        )
                                        .map((therapist) => (
                                            <TherapistCard
                                                key={therapist.doctor_id}
                                                item={therapist}
                                                selectedDoctorId={selectedDoctorId}
                                                onClick={() => setSelectedDoctorId(therapist.doctor_id)}
                                            />
                                        ))}

                                </div>
                            </div>
                        </div>
                    )}

                    <SessionDetail />

                    <div className="ChooseTherapistContainer">
                        <h1>1. Select a Date</h1>
                        <div className="TherapistCards DateCardWrapper2">
                            {allAvailableDates.map((slot) => (
                                <DateCard
                                    key={slot.date}
                                    data={slot}
                                    selectedDate={selectedDate}
                                    setselectedDate={setSelectedDate}
                                    allTimeSlots={allTimeSlots}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="TimingHeading">
                        <h1>2. Select Available Slot</h1>
                        <h2 className="Datesunheading">
                            Showing slots for: <span className="text-[#FE8235]">{selectedDate}</span>
                        </h2>

                        {["Morning", "Noon", "Evening"].map((period, i) => {
                            const ranges = {
                                Morning: [7, 12],
                                Noon: [12, 17],
                                Evening: [17, 22],
                            };
                            const [start, end] = ranges[period];
                            const slots = renderTimeSlots(start, end);
                            return slots.length ? (
                                <div className="MorningDiv" key={period}>
                                    <div className="MorningDivHeading">
                                        <p>{period}</p>
                                        <p>
                                            {start}:00 - {end}:00
                                        </p>
                                    </div>
                                    <div className="TimeSlots">{slots}</div>
                                </div>
                            ) : null;
                        })}
                    </div>

                    <div className="flex justify-center mt-4">
                        <button
                            className={`offer-gradient text-center text-white font-semibold text-lg px-6 py-2 my-4 rounded-xl ${selectedTimeSlot ? "" : "opacity-50 cursor-not-allowed"
                                }`}
                            disabled={!selectedTimeSlot}
                            onClick={handleProceed}
                        >
                            <span className="flex items-center gap-2">
                                Continue to Booking Details
                                <img src={rightIcon} alt="arrow" className="w-6 h-5 invert brightness-0" />
                            </span>
                        </button>
                    </div>
                </div>
            </div>

        </>
    );
};

export default SlotPageFreelancer;
